import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, Download, Search, X, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const ReportingPerformance = () => {
  const { filialeId, isConsolidated } = useFiliale();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [periodeInfo, setPeriodeInfo] = useState(null);
  const [indicateurs, setIndicateurs] = useState([]);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'employe', dir: 'asc' });
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { date };
        if (!isConsolidated && filialeId && filialeId !== 'all') params.filiale_id = filialeId;
        console.log('Reporting Performance - isConsolidated:', isConsolidated, 'filialeId:', filialeId, 'params:', params);
        const res = await http.get('/api/paie/reporting-performance', { params });
        if (!cancelled) {
          const responseData = Array.isArray(res.data) ? res.data : [];
          setData(responseData);
          
          // Extraire les indicateurs uniques
          const uniqueIndicateurs = [...new Map(responseData.map(r => [r.idindicateur_has_type_personnel, {
            id: r.idindicateur_has_type_personnel,
            libelle: r.Libelle_indicateur
          }])).values()];
          setIndicateurs(uniqueIndicateurs);
          
          if (responseData.length > 0) {
            setPeriodeInfo({
              libelle: responseData[0].libelle_paie || `Période du ${date}`,
              etat: responseData[0].etat || 2
            });
          }
        }
      } catch (e) {
        console.error('Erreur récupération données performance:', e);
        if (!cancelled) {
          setData([]);
          setIndicateurs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, filialeId, isConsolidated]);

  // Grouper les données par employé
  const groupedData = useMemo(() => {
    const grouped = {};
    data.forEach(row => {
      if (!grouped[row.idpersonnel]) {
        grouped[row.idpersonnel] = {
          idpersonnel: row.idpersonnel,
          matricule: row.matricule,
          nom: row.nom,
          prenom: row.prenom,
          business_unit: row.business_unit,
          filiale_code: row.filiale_code,
          type_personnel: row.type_personnel,
          indicateurs: {}
        };
      }
      grouped[row.idpersonnel].indicateurs[row.idindicateur_has_type_personnel] = row.valeur;
    });
    return Object.values(grouped);
  }, [data]);

  const filteredData = useMemo(() => {
    if (!query) return groupedData;
    const q = query.toLowerCase();
    return groupedData.filter((r) => 
      (r.nom && r.nom.toLowerCase().includes(q)) ||
      (r.prenom && r.prenom.toLowerCase().includes(q)) ||
      (r.matricule && r.matricule.toLowerCase().includes(q))
    );
  }, [groupedData, query]);

  const sortedData = useMemo(() => {
    const { key, dir } = sort;
    return [...filteredData].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === 'employe') {
        aVal = `${a.nom} ${a.prenom}`;
        bVal = `${b.nom} ${b.prenom}`;
      }
      if (aVal === bVal) return 0;
      const cmp = aVal < bVal ? -1 : 1;
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sort]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleExport = () => {
    const headers = ['Matricule', 'Nom', 'Prénom', 'Business Unit', 'Type Personnel', 'Filiale', ...indicateurs.map(i => i.libelle)];
    const lines = sortedData.map((r) => [
      r.matricule || '',
      r.nom || '',
      r.prenom || '',
      r.business_unit || '',
      r.type_personnel || '',
      r.filiale_code || '',
      ...indicateurs.map(i => r.indicateurs[i.id] || 0)
    ]);
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '\uFEFF' + [headers, ...lines].map((l) => l.map(esc).join(';')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    link.download = `reporting-performance-${date}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!date) {
    return (
      <div className="p-6">
        <BlueHeader title="Reporting Performance" subtitle="Indicateurs de performance par agent" />
        <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
          <BarChart3 className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="font-medium text-gray-700">Aucune période sélectionnée</p>
          <p className="mt-1 text-sm text-gray-500">Choisissez une période de paie pour afficher le reporting performance.</p>
        </div>
      </div>
    );
  }

  const SortIcon = ({ k }) => {
    const on = sort.key === k;
    const Icon = on && sort.dir === 'asc' ? ChevronUp : ChevronDown;
    return <Icon className={`h-3.5 w-3.5 ${on ? 'text-blue-600' : 'text-gray-300'}`} />;
  };

  return (
    <div className="p-6">
      <BlueHeader 
        title="Reporting Performance" 
        subtitle={periodeInfo ? `Indicateurs de performance - ${periodeInfo.libelle}` : 'Indicateurs de performance par agent'} 
      />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par nom, prénom, matricule..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-blue-700"
        >
          <Download className="h-4 w-4" />
          Exporter CSV
        </button>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
        </div>
      ) : groupedData.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
          <BarChart3 className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="font-medium text-gray-700">Aucune donnée de performance trouvée</p>
          <p className="mt-1 text-sm text-gray-500">Il n'y a pas d'indicateurs de performance pour cette période.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    <button onClick={() => handleSort('employe')} className="flex items-center gap-1 hover:text-blue-600">
                      Employé <SortIcon k="employe" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    <button onClick={() => handleSort('matricule')} className="flex items-center gap-1 hover:text-blue-600">
                      Matricule <SortIcon k="matricule" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    <button onClick={() => handleSort('business_unit')} className="flex items-center gap-1 hover:text-blue-600">
                      Business Unit <SortIcon k="business_unit" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">
                    Type Personnel
                  </th>
                  {indicateurs.map(ind => (
                    <th key={ind.id} className="px-4 py-3 text-right font-medium text-gray-700">
                      {ind.libelle}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {row.nom} {row.prenom}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.matricule}</td>
                    <td className="px-4 py-3 text-gray-600">{row.business_unit}</td>
                    <td className="px-4 py-3 text-gray-600">{row.type_personnel}</td>
                    {indicateurs.map(ind => (
                      <td key={ind.id} className="px-4 py-3 text-right text-gray-900">
                        {row.indicateurs[ind.id] || 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
              <div className="text-sm text-gray-600">
                Affichage de {(page - 1) * pageSize + 1} à {Math.min(page * pageSize, sortedData.length)} sur {sortedData.length} résultats
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg p-2 text-gray-600 transition-all hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} sur {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="rounded-lg p-2 text-gray-600 transition-all hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportingPerformance;
